import {
  PolicyEscalationType,
  PolicyTargetStatus,
  PolicyType,
} from 'shared';
import {
  useEffect,
  useMemo,
  useState,
} from 'react';

import { fieldIsValid } from '../../../../lib/helper';
import { IPolicyProps } from '../PolicyItem';
import { InputTextAddon } from '../../../../../common/Atoms/InputTextAddon';
import { RadioStack } from '../../../../../common/Atoms/RadioStack';
import { Grid } from '../../../../../common/Atoms/Grid';
import { GridItem } from '../../../../../common/Atoms/GridItem';
import { TextEditor } from '../../../Molecules/TextEditor/TextEditor';
import { Heading } from '../../../../../common/Atoms/Typography/Heading';
import { FormDescription } from '../../../../../common/Atoms/FormDescription';
import { FormLabel } from '../../../../../common/Atoms/FormLabel';
import { Select } from '../../../../../common/Atoms/Select';
import { DefaultAssignee } from '../PolicyForm/Fields/DefaultAssignee';
import { RadioCards } from '../../../../../common/Atoms/RadioCards';
import { SenderProfileSelector } from '../PolicyForm/SenderProfileSelector';

const escalationOptions = [
  {
    title: `Escalate to call`,
    description: `This will place an escalation in the queue for your team to call this customer.`,
    value: PolicyEscalationType.CALL,
  },
  {
    title: `Escalate to stop credit`,
    description: `This will remind your team to cease any further credit sales to the customer.`,
    value: PolicyEscalationType.STOP_CREDIT,
  },
  {
    title: `Escalate template legal letter`,
    description: `This will place an escalation in the queue for your team to send a template legal letter.`,
    value: PolicyEscalationType.LETTER,
  },
  {
    title: `Other`,
    description: `This will place an escalation in the queue for your team. Use the instructions to say what needs to happen.`,
    value: PolicyEscalationType.OTHER,
  },
];

const triggerOptions = [
  {
    title: `Contact Values`,
    description: `Use values of a contact record to determine when to raise an escalation.`,
    value: PolicyType.CONTACT_ESCALATION,
  },
  {
    title: `Invoice Values`,
    description: `Use values of an invoice to determine when to raise an escalation.`,
    value: PolicyType.ESCALATION,
  },
];

function calculateDayTime(value) {
  if (value === 0) {
    return `on`;
  }

  if (value < 0) {
    return `before`;
  }

  return `after`;
}

export function PolicyContactEscalation({
  policy,
  invalids,
  handleChange,
  hasCompletedActions,
}: IPolicyProps) {
  const [dayType, setDayType] = useState(calculateDayTime(policy.day_index));

  useEffect(() => {
    setDayType(calculateDayTime(policy.day_index));
  }, [ policy.day_index ]);

  function handleStepsContentChange({ text }) {
    handleChange({
      name: `escalation_steps`,
      value: text,
    });
  }

  const dayTypes = useMemo(() => {
    const result =[
      {
        label: `After`,
        value: `after`,
      },
      {
        label: `On The`,
        value: `on`,
      },
    ];

    if (policy.target_status === PolicyTargetStatus.DUE) {
      result.push({
        label: `Before`,
        value: `before`,
      });
    }

    return result;
  }, [ policy.target_status ]);

  const states = [
    {
      label: `Oldest Due Date`,
      value: PolicyTargetStatus.OLDEST_DUE,
    },
  ];

  const dayIndexValue = useMemo(() => {
    let str = policy.day_index.toString();

    if (str.startsWith(`-`)) {
      str = str.replace(`-`, ``);
    }

    return str;
  }, [ policy.day_index ]);

  function onDayIndexChange(e, day = dayType) {
    const { value } = e;

    let num = Number(value);

    if (Number.isNaN(num)) {
      handleChange({
        name: `day_index`,
        value,
      });

      return;
    }

    if (num !== 0) {
      if (day === `before`) {
        num = num < 0 ? num : -num;
      }

      if (day === `after`) {
        num = num > 0 ? num : -num;
      }

      if (day === `on`) {
        num = 0;
      }
    }

    handleChange({
      name: `day_index`,
      value: num,
    });
  }

  function handleDayTypeChange(e) {
    setDayType(e.value);

    onDayIndexChange({
      value: policy.day_index,
    }, e.value);
  }

  return (
    <>
      <RadioCards
        options={ triggerOptions }
        onChange={ handleChange }
        selected={ policy.policy_type }
        cols={ 2 }
        name={ `policy_type` }
        disabled={ hasCompletedActions }
        tooltip={ hasCompletedActions ? `This policy has completed actions. To change type, delete and create a new policy` : undefined }
      />
      <RadioStack
        options={ escalationOptions }
        onChange={ handleChange }
        selected={ policy.escalation_type }
        name={ `escalation_type` }
      />

      <div>
        <FormLabel>{ `Raise this escalation` }</FormLabel>
        <Grid cols={ 3 }
          className={ `items-end` }>
          <GridItem>
            <InputTextAddon
              value={ dayIndexValue }
              onChange={ onDayIndexChange }
              name={ `day_index` }
              invalid={ fieldIsValid(`day_index`, invalids) }
              addOnText={ `days ` }
              disabled={ dayType === `on` }
            />
          </GridItem>
          <GridItem>
            <Select
              options={ dayTypes }
              selected={ dayType }
              onChange={ handleDayTypeChange }
            />
          </GridItem>
          <GridItem>
            <Select
              options={ states }
              selected={ policy.target_status }
              onChange={ handleChange }
              name={ `target_status` }
              invalid={ fieldIsValid(`target_status`, invalids) }
            />
          </GridItem>
        </Grid>
      </div>

      { /* TODO: should we allow merge tags in this field? */ }
      <Heading>{ `Instructions` }</Heading>
      <FormDescription>
        { `Optionally enter any instructions here. For example the discussion topics for a phone call, or which template legal letter to send.` }
      </FormDescription>
      <TextEditor
        value={ policy.escalation_steps }
        onChange={ handleStepsContentChange }
        mode={ `note` }
      />

      <DefaultAssignee
        policyFormData={ policy }
        invalids={ invalids }
        handleChange={ handleChange }
      />

      <InputTextAddon
        value={ policy.escalation_emails || `` }
        onChange={ handleChange }
        label={ `Notify email addresses` }
        subHeading={ `Enter email addresses to notify when this escalation is scheduled` }
        name={ `escalation_emails` }
        invalid={ fieldIsValid(`escalation_emails`, invalids) }
        helpIcon= { `Enter up to 5 email addresses, separated by a comma` }
      />

      <SenderProfileSelector
        handleChange={ handleChange }
        policyFormData={ policy }
        invalids={ invalids }
        anchor={ `top` }
      />
    </>
  );
}
